import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Wallet, FileText, TrendingUp, TrendingDown, Receipt, Landmark, ShieldAlert, ExternalLink, Info } from "lucide-react";
import { formatIDR, fmtDate } from "@/lib/constants";
// 2026-06-20 (user request "REKONSILIASI PROGRESS PEMBAYARAN LINTAS HALAMAN"):
// KPI Sisa Piutang, Uang Masuk, dsb. WAJIB membaca dari SATU sumber
// (summary.payment_breakdown backend) — jangan hitung `outstanding = Bruto −
// UangMasuk` di sini (itu bug lama selisih Rp202.668 di RKI Tanjung Morawa).
import { breakdownFromSummary, PAYMENT_FORMULA_TOOLTIPS } from "@/lib/paymentBreakdown";
// 2026-10-22 (user request): 4 blok klasifikasi penerimaan — Pembayaran Resmi,
// DP/Uang Muka, Retensi, Belum Terklasifikasi.
import ReceiptBreakdownBlocks from "./ReceiptBreakdownBlocks";

/**
 * KeuanganTab — Fase 3 (read-only). KPI + daftar invoice (diterima, sisa,
 * jatuh tempo, status) + ringkasan pajak/retensi + daftar pengeluaran.
 * Sumber data: GET /active-projects/{pid}/monitoring (sama dengan tab lain).
 */
const INV_STATUS = {
  paid: { label: "Lunas", cls: "bg-emerald-50 border-emerald-300 text-emerald-700" },
  partial: { label: "Sebagian", cls: "bg-amber-50 border-amber-300 text-amber-800" },
  unpaid: { label: "Belum Dibayar", cls: "bg-rose-50 border-rose-300 text-rose-700" },
};

export default function KeuanganTab({ mon, canViewFinance = true }) {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const pid = mon?.project_id;
  // 2026-10-21 (user request A): deep-link ?focus_invoice=<eid> dari Collection
  // → scroll + highlight baris invoice yang sesuai (bukan dashboard).
  const focusInvoice = sp.get("focus_invoice");
  useEffect(() => {
    if (!focusInvoice) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="keu-inv-${focusInvoice}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#FF5A1F]", "bg-amber-50");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#FF5A1F]"), 2500);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [focusInvoice, mon]);
  const s = mon?.summary || {};
  const invoices = mon?.invoices || [];
  const expenses = mon?.expense_entries || [];
  const tr = mon?.tax_retention || {};
  const exp = Number(s.total_expense || 0);
  // SATU sumber angka (spec 2026-06-20): summary.payment_breakdown.
  // Semua KPI di halaman ini membaca dari sana → konsisten dgn Ringkasan,
  // Cash Flow, Laporan Keuangan, dan Export.
  const bd = breakdownFromSummary(s);
  // 2026-06-21 (user request): Nilai Kontrak = JUMLAHAN induk + VO/WO
  // (field total_value dari engine) — bukan lagi kontrak induk saja.
  const cv = bd?.totalValue ?? Number(s.contract_value || 0);
  const invoiceGross = bd?.invoiceGrossTotal ?? invoices.reduce((a, x) => a + Number(x.invoice_amount || 0), 0);
  const cashDue = bd?.cashDueTotal ?? invoiceGross;
  const received = bd?.receivedTotal ?? Number(s.total_income || 0);
  const overdue = bd?.overdueReceivable ?? Math.max(0, cashDue - received);
  const retentionNotYetDue = bd?.retentionNotYetDue ?? 0;
  // 2026-10-21 (user request B): pemisahan RESMI vs DRAFT — semua angka dari
  // engine backend (payment_breakdown), frontend tidak menghitung ulang.
  const officialReceived = bd?.officialReceived ?? received;
  const officialOutstanding = bd?.officialOutstanding ?? overdue;
  const uvCount = bd?.unverifiedInvoiceCount ?? 0;
  const uv = {
    count: uvCount,
    gross: bd?.unverifiedGrossAmount ?? 0,
    cashDue: bd?.unverifiedCashDue ?? 0,
    received: bd?.unverifiedReceived ?? 0,
    outstanding: bd?.unverifiedOutstanding ?? 0,
    noDue: bd?.unverifiedNoDueCount ?? 0,
  };
  // Filter tabel invoice: Semua / Resmi (Terverifikasi) / Draft (Perlu Verifikasi).
  const [invFilter, setInvFilter] = useState("all");
  const isDraftInv = (i) => (i?.verification_state ?? "terverifikasi") === "perlu_verifikasi";
  const isOfficialInv = (i) => (i?.included_in_official_ar ?? !isDraftInv(i)) === true;
  const visibleInvoices = invFilter === "official" ? invoices.filter(isOfficialInv)
    : invFilter === "draft" ? invoices.filter(isDraftInv)
    : invoices;
  const officialCount = invoices.filter(isOfficialInv).length;
  const draftCount = invoices.filter(isDraftInv).length;

  // 2026-06-20 (user request, project "VO dipisah"): invoice dikelompokkan per
  // sumber — Main Project dulu, lalu VO, lalu WO — saat ada >1 sumber. Tiap
  // grup menampilkan history invoice + status bayar masing-masing.
  const invGroups = useMemo(() => {
    const order = { main: 0, vo: 1, wo: 2 };
    const map = new Map();
    for (const inv of visibleInvoices) {
      const kind = inv.source_kind || "main";
      const name = inv.source_name || inv.source_project_name || "Main Project";
      const key = `${kind}::${name}`;
      if (!map.has(key)) map.set(key, { kind, name, items: [] });
      map.get(key).items.push(inv);
    }
    return [...map.values()].sort((a, b) => (order[a.kind] ?? 9) - (order[b.kind] ?? 9) || a.name.localeCompare(b.name));
  }, [visibleInvoices]);
  const multiSource = invGroups.length > 1;
  const GROUP_BADGE = {
    main: { label: "Main Project", cls: "bg-[#0B1D3A] text-white border-[#0B1D3A]" },
    vo: { label: "VO", cls: "bg-orange-50 border-orange-300 text-[#FF5A1F]" },
    wo: { label: "WO", cls: "bg-sky-50 border-sky-300 text-sky-700" },
  };

  if (!canViewFinance) return (
    <div className="bg-white border border-slate-200 p-6 text-center" data-testid="keu-no-access">
      <ShieldAlert size={20} className="mx-auto text-slate-300 mb-2" />
      <div className="text-xs text-slate-500">Anda tidak memiliki akses melihat keuangan project (butuh permission Laporan Keuangan → Lihat).</div>
    </div>
  );

  // Setiap KPI WAJIB punya tooltip rumus (spec user 2026-06-20).
  const kpis = [
    { icon: Wallet, label: "Nilai Kontrak",
      value: cv > 0 ? formatIDR(cv) : "—",
      tone: "navy",
      testid: "keu-nilai-kontrak",
      formula: PAYMENT_FORMULA_TOOLTIPS.contract_value,
      valueSize: "text-lg" },
    { icon: FileText, label: "Total Invoice Bruto Resmi", value: formatIDR(invoiceGross), tone: "navy",
      testid: "keu-total-ditagihkan", formula: PAYMENT_FORMULA_TOOLTIPS.invoice_gross + " Hanya invoice resmi terverifikasi — draft & klaim retensi tidak termasuk." },
    { icon: Landmark, label: "Kas Harus Diterima Resmi", value: formatIDR(cashDue), tone: "navy",
      testid: "keu-kas-harus-diterima", formula: PAYMENT_FORMULA_TOOLTIPS.cash_due + " Hanya invoice resmi terverifikasi — draft & klaim retensi tidak termasuk." },
    { icon: TrendingUp, label: "Uang Sudah Diterima Resmi", value: formatIDR(officialReceived), tone: "green",
      testid: "keu-uang-masuk", formula: PAYMENT_FORMULA_TOOLTIPS.received + " Hanya invoice resmi terverifikasi." },
    { icon: Receipt, label: "Sisa Piutang Resmi", value: formatIDR(officialOutstanding),
      tone: officialOutstanding > 0 ? "amber" : "navy", testid: "keu-sisa-piutang",
      formula: PAYMENT_FORMULA_TOOLTIPS.overdue + " Hanya invoice resmi terverifikasi — draft & klaim retensi tidak termasuk." },
    { icon: Receipt, label: "Retensi Belum Jatuh Tempo", value: formatIDR(retentionNotYetDue),
      tone: "sky", testid: "keu-retensi-belum-jt", formula: PAYMENT_FORMULA_TOOLTIPS.retention_not_yet_due },
    { icon: TrendingDown, label: "Uang Keluar", value: formatIDR(exp), tone: "red",
      testid: "keu-uang-keluar", formula: "Uang Keluar = Σ expense_entries (biaya proyek + Cost Control)." },
    { icon: Wallet, label: "Posisi Kas Bersih", value: formatIDR(received - exp),
      tone: received - exp < 0 ? "red" : "navy", testid: "keu-posisi-kas",
      formula: "Posisi Kas Bersih = Uang Sudah Diterima − Uang Keluar." },
  ];

  return (
    <div className="space-y-4" data-testid="keuangan-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-2" data-testid="keu-kpis">
        {kpis.map((k) => {
          const toneCls = k.tone === "red" ? "text-rose-700" : k.tone === "green" ? "text-emerald-700" : k.tone === "amber" ? "text-amber-700" : k.tone === "sky" ? "text-sky-700" : "text-[#0B1D3A]";
          return (
            <div key={k.label} className="bg-white border border-slate-200 p-3 relative group" data-testid={k.testid} title={k.formula}>
              <div className="flex items-center gap-1.5">
                <k.icon size={13} className={toneCls} />
                <span className="overline text-[9px] text-slate-500 tracking-wider truncate">{k.label}</span>
                <Info size={10} className="ml-auto text-slate-300 group-hover:text-slate-500 transition-colors shrink-0" aria-label={`Rumus: ${k.formula}`} />
              </div>
              <div className={`font-display ${k.valueSize || "text-lg"} tabular-nums mt-1 ${toneCls}`}>{k.value}</div>
            </div>
          );
        })}
      </div>

      {/* 2026-10-22 (user request "PERBAIKAN KLASIFIKASI PENERIMAAN PROYEK"):
          4 blok terpisah — Pembayaran Resmi, DP/Uang Muka, Retensi, Belum
          Terklasifikasi — dari engine kanonis backend. */}
      <ReceiptBreakdownBlocks bd={bd} pid={pid} invoices={invoices} />

      {/* 2026-10-21 (user request B): keterangan cakupan kartu resmi + strip
          invoice draft/perlu verifikasi — angka dari engine backend. */}
      <div className="text-[10px] text-slate-500 italic" data-testid="keu-official-scope-note">
        Kartu bertanda “Resmi” hanya mencakup invoice terverifikasi. Invoice draft ditampilkan terpisah di bawah.
      </div>
      {uvCount > 0 && (
        <div className="border border-amber-300 bg-amber-50" data-testid="keu-draft-strip">
          <div className="px-3 py-2 border-b border-amber-200 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-900">Invoice Draft / Perlu Verifikasi</span>
            <span className="text-[10px] text-amber-800">— belum masuk piutang resmi sampai diverifikasi FAT</span>
            <button type="button" data-testid="keu-draft-strip-drill"
              onClick={() => setInvFilter("draft")}
              className="ml-auto px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-amber-400 bg-white text-amber-900 hover:bg-amber-100">
              Lihat {uvCount} baris
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-amber-100">
            {[
              { label: "Jumlah Invoice", val: `${uv.count}`, testid: "draft-count" },
              { label: "Total Bruto", val: formatIDR(uv.gross), testid: "draft-gross" },
              { label: "Kas Harus Diterima", val: formatIDR(uv.cashDue), testid: "draft-cash-due" },
              { label: "Sudah Diterima", val: formatIDR(uv.received), testid: "draft-received" },
              { label: "Sisa (Informatif)", val: formatIDR(uv.outstanding), testid: "draft-outstanding" },
              { label: "Tanpa Due Date", val: `${uv.noDue}`, testid: "draft-no-due" },
            ].map((c) => (
              <div key={c.testid} className="bg-amber-50 px-3 py-2" data-testid={`keu-${c.testid}`}>
                <div className="text-[9px] uppercase tracking-wider font-semibold text-amber-800/80">{c.label}</div>
                <div className="text-sm font-bold tabular-nums text-amber-900">{c.val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daftar invoice — 2026-06-19 (user request): klik menghubungkan ke
          Laporan Keuangan → tab "Invoice & Piutang" (prefilter project ini). */}
      <div className="bg-white border border-slate-200" data-testid="keu-invoice-table">
        <button type="button" data-testid="keu-invoice-goto-finance"
          onClick={() => pid && nav(`/finance-report?tab=invoice-piutang&project_id=${pid}&from=monitoring`)}
          title="Buka daftar invoice di Laporan Keuangan"
          className="w-full px-4 py-2.5 border-b border-slate-200 flex items-center gap-2 hover:bg-slate-50 transition-colors text-left group">
          <FileText size={14} className="text-[#0B1D3A]" />
          <h3 className="text-sm font-semibold text-[#0B1D3A] group-hover:underline">Daftar Invoice</h3>
          <span className="text-[10px] text-slate-400 tabular-nums">{invoices.length} invoice</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-400 group-hover:text-[#0B1D3A]">
            Laporan Keuangan <ExternalLink size={11} />
          </span>
        </button>
        {/* 2026-10-21 (user request B.8): filter Semua / Resmi / Draft — status
            pembayaran & verifikasi adalah dua dimensi terpisah. */}
        {invoices.length > 0 && (officialCount !== invoices.length || draftCount > 0) && (
          <div className="px-4 py-1.5 border-b border-slate-200 flex items-center gap-1" data-testid="keu-inv-filter">
            {[
              { k: "all", label: `Semua (${invoices.length})` },
              { k: "official", label: `Resmi/Terverifikasi (${officialCount})` },
              { k: "draft", label: `Draft/Perlu Verifikasi (${draftCount})` },
            ].map((f) => (
              <button key={f.k} type="button" data-testid={`keu-inv-filter-${f.k}`}
                onClick={() => setInvFilter(f.k)}
                className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider border ${invFilter === f.k ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                {f.label}
              </button>
            ))}
          </div>
        )}
        {invoices.length === 0 ? (
          <div className="p-6 text-center text-[11px] text-slate-400" data-testid="keu-invoice-empty">Belum ada invoice tercatat untuk project ini.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[720px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                {/* 2026-06-20 (user request): kolom "Tanggal" diperjelas → "Tanggal Invoice". */}
                <tr className="text-left">{["No. Invoice", "Tanggal Invoice", "Bruto", "Kas Harus Diterima", "Sudah Diterima", "Sisa Piutang", "Jatuh Tempo", "Status", ...(multiSource ? [] : ["Sumber"])].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600" title={h === "Sisa Piutang" ? PAYMENT_FORMULA_TOOLTIPS.overdue : h === "Kas Harus Diterima" ? PAYMENT_FORMULA_TOOLTIPS.cash_due : undefined}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {invGroups.map((g, gi) => {
                  const gb = GROUP_BADGE[g.kind] || GROUP_BADGE.main;
                  const subtotal = g.items.reduce((a, x) => a + Number(x.invoice_amount || 0), 0);
                  return [
                    multiSource && (
                      <tr key={`grp-${gi}`} className="bg-slate-100/70 border-b border-slate-200" data-testid={`keu-inv-group-${gi}`}>
                        <td colSpan={7} className="px-3 py-1.5">
                          <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border mr-2 ${gb.cls}`} data-testid={`keu-inv-group-badge-${gi}`}>{gb.label}</span>
                          <span className="text-[11px] font-semibold text-[#0B1D3A]">{g.name}</span>
                          <span className="text-[10px] text-slate-400 ml-2 tabular-nums">{g.items.length} invoice · {formatIDR(subtotal)}</span>
                        </td>
                      </tr>
                    ),
                    ...g.items.map((inv) => {
                      const sm = INV_STATUS[inv.status] || INV_STATUS.unpaid;
                      return (
                        <tr key={inv.id} data-testid={`keu-inv-${inv.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 font-medium text-[#0B1D3A]">{inv.invoice_no}</td>
                          <td className="px-3 py-2 tabular-nums text-slate-500">{inv.invoice_date ? fmtDate(inv.invoice_date) : "—"}</td>
                          <td className="px-3 py-2 tabular-nums text-slate-700" title="Nilai Invoice Bruto (sebelum PPh & retensi)">{formatIDR(inv.invoice_amount)}</td>
                          <td className="px-3 py-2 tabular-nums text-[#0B1D3A]" title={PAYMENT_FORMULA_TOOLTIPS.cash_due}>{formatIDR(inv.expected_net || inv.invoice_amount)}</td>
                          <td className="px-3 py-2 tabular-nums text-emerald-700">{formatIDR(inv.received)}</td>
                          <td className={`px-3 py-2 tabular-nums ${inv.outstanding > 0 ? "text-amber-700" : "text-slate-400"}`} title={PAYMENT_FORMULA_TOOLTIPS.overdue}>{formatIDR(inv.outstanding)}</td>
                          <td className="px-3 py-2 tabular-nums text-slate-500">{inv.due_date ? fmtDate(inv.due_date) : "—"}</td>
                          {/* Tanggal bayar dicantumkan di sebelah status (2026-06-20, user request). */}
                          <td className="px-3 py-2 whitespace-nowrap">
                            {/* 2026-10-21 (user request B.7): badge pembayaran
                                dan verifikasi dipisah — draft tetap punya
                                payment_state tapi ditandai belum masuk resmi. */}
                            <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${sm.cls}`} data-testid={`keu-inv-status-${inv.id}`}>{sm.label}</span>
                            {inv.verification_state === "perlu_verifikasi" && (
                              <span className="ml-1 inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-amber-400 bg-amber-50 text-amber-800"
                                data-testid={`keu-inv-draft-badge-${inv.id}`}
                                title="Draft / Perlu Verifikasi — baris ini belum masuk ringkasan resmi">
                                Draft — Perlu Verifikasi
                              </span>
                            )}
                            {inv.payment_date && <span className="ml-1.5 text-[10px] text-slate-500 tabular-nums" data-testid={`keu-inv-paydate-${inv.id}`}>· {fmtDate(inv.payment_date)}</span>}
                          </td>
                          {!multiSource && <td className="px-3 py-2 text-[10px] text-slate-500">{inv.source_name || inv.source_project_name || "—"}</td>}
                        </tr>
                      );
                    }),
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ringkasan pajak & retensi */}
      <div className="bg-white border border-slate-200" data-testid="keu-tax-summary">
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2">
          <Landmark size={14} className="text-[#0B1D3A]" />
          <h3 className="text-sm font-semibold text-[#0B1D3A]">Ringkasan Pajak & Retensi</h3>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {[
            { label: "Total PPN", value: bd?.ppnTotal ?? tr.ppn, testid: "keu-tax-ppn", formula: PAYMENT_FORMULA_TOOLTIPS.ppn },
            { label: "Total PPh", value: bd?.pphTotal ?? tr.pph, testid: "keu-tax-pph", formula: PAYMENT_FORMULA_TOOLTIPS.pph },
            { label: "Retensi Ditahan", value: bd?.retentionTotal ?? tr.retention, testid: "keu-tax-retention", formula: PAYMENT_FORMULA_TOOLTIPS.retention },
            { label: "Retensi Dicairkan", value: tr.retention_released, testid: "keu-tax-retention-released", formula: "Retensi Dicairkan = Σ retention_released_amount (uang retensi yang sudah dicairkan client)." },
          ].map((x) => (
            <div key={x.label} data-testid={x.testid} title={x.formula}>
              <div className="overline text-[9px] text-slate-500 flex items-center gap-1">{x.label} <Info size={9} className="text-slate-300" /></div>
              <div className="tabular-nums font-semibold text-[#0B1D3A] mt-0.5">{x.value ? formatIDR(x.value) : <span className="text-slate-300">—</span>}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Daftar pengeluaran */}
      <div className="bg-white border border-slate-200" data-testid="keu-expense-table">
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2">
          <TrendingDown size={14} className="text-rose-700" />
          <h3 className="text-sm font-semibold text-[#0B1D3A]">Pengeluaran Project</h3>
          <span className="text-[10px] text-slate-400 tabular-nums">{expenses.length} entri</span>
        </div>
        {expenses.length === 0 ? (
          <div className="p-6 text-center text-[11px] text-slate-400" data-testid="keu-expense-empty">Belum ada pengeluaran tercatat.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[560px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">{["Tanggal", "Keterangan", "Kategori", "Nilai", "Sumber"].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}</tr>
              </thead>
              <tbody>
                {expenses.map((ex, i) => (
                  <tr key={ex.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 tabular-nums text-slate-500">{ex.date ? fmtDate(ex.date) : "—"}</td>
                    <td className="px-3 py-2 text-[#0B1D3A]">{ex.description || ex.name || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{ex.category || "—"}</td>
                    <td className="px-3 py-2 tabular-nums text-rose-700">{formatIDR(ex.amount || 0)}</td>
                    <td className="px-3 py-2 text-[10px] text-slate-500">{ex._source_project_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
